import axiosInstance, {
  errorHandler,
  totalAccountsBranchTypeAdapter,
} from "./api";
import retry from "p-retry";
import { SortAdapter } from "./utils";
import qs from "qs";

const requestAccountStatement = axios => ({
  currency,
  accountId,
  periodInMonth = 0,
}) => {
  return axios
    .get(`/report-service/api/v2/export/account_statement`, {
      params: {
        currency,
        accountId,
        periodInMonth,
      },
    })
    .then(({ data }) => ({ reportId: data.data }))
    .catch(errorHandler);
};

/**
 * Generate reports with incoming/outgoing iroha transactions
 */
const requestTransactions = axios => direction => params => {
  params.type = params.type || "ALL";
  params.unpaged = params.unpaged || true;
  return axios
    .get(`/report-service/api/export/iroha-transactions/${direction}`, {
      params: params,
      paramsSerializer: params =>
        qs.stringify(params, { arrayFormat: "repeat" }),
      // {
      //   page,
      //   size,
      //   filter,
      //   sort,
      //   type,
      //   from,
      //   to,
      //   unpaged,
      // }
    })
    .then(({ data: { id } }) => ({ reportId: id }))
    .catch(errorHandler);
};

// ReportResource.getIrohaTransactionsByAccountId
/**
 * Generate reports with all transactions made by given account ID
 */
const requestAccountTransactions = axios => ({
  id,
  page,
  size,
  filter,
  sort,
  type = "ALL",
  from,
  to,
  unpaged = true,
}) => {
  if (!id || id === "undefined") throw new Error("Wrong account ID");

  return axios
    .get(`/report-service/api/v2/export/iroha-transactions/account-details`, {
      params: {
        accountId: id,
        page,
        size,
        filter,
        sort,
        type,
        from,
        to,
        unpaged,
      },
    })
    .then(({ data: { id } }) => ({ reportId: id }))
    .catch(errorHandler);
};

const requestDownloadExportedFile = axios => reportId => {
  return axios
    .get(`/report-service/api/v2/report/download/${reportId}`, {
      responseType: "blob",
      headers: {
        Accept: "application/octet-stream",
      },
    })
    .then(({ data }) => data)
    .catch(errorHandler);
};

const requestCancelExportedFile = axios => reportId => {
  return axios
    .put(`/report-service/api/v2/report/cancel/${reportId}`)
    .then(({ data }) => data)
    .catch(errorHandler);
};

const requestExports = axios => ({
  page = 0,
  size = 10,
  filter,
  sort = "createdAt,Desc",
  status,
  from,
  to,
}) => {
  return axios
    .get(`/report-service/api/v2/report/list`, {
      params: {
        page,
        size,
        filter,
        sort,
        status,
        from,
        to,
      },
    })
    .then(({ data }) => ({ data }))
    .catch(errorHandler);
};

// ReportResource.getIncomingIrohaTransactions
const requestIncomingTransactions = axios =>
  requestTransactions(axios)("incoming");
// ReportResource.getOutgoingIrohaTransactions
const requestOutgoingTransactions = axios =>
  requestTransactions(axios)("outgoing");

// ReportResource.getAllFstIrohaAccounts
/**
 * Generate reports with all iroha accounts
 */
const requestListUserUnderDomain = axios => ({
  page,
  size,
  filter = "",
  sort = "",
  type = "ALL",
  branch = "ALL",
  from,
  to,
  unpaged = true,
}) =>
  axios
    .get(`/tps/api/report/list-iroha-account`, {
      params: {
        page,
        size,
        filter,
        sort: SortAdapter(sort),
        branch: totalAccountsBranchTypeAdapter(branch),
        type,
        from,
        to,
        unpaged,
      },
    })
    .then(({ data: { id } }) => ({ reportId: id }))
    .catch(errorHandler);

// ReportResource.getWebTransactionsHistory
/**
 * Generate reports with web transaction history
 */
const requestHistory = axios => ({
  currency,
  account,
  from,
  to,
  status,
  page,
  size,
  filter = "",
  sort = "",
  unpaged = true,
}) =>
  axios
    .get(`/report-service/api/export/fast-transactions/web-history`, {
      params: {
        currency,
        account,
        from,
        to,
        status,
        page,
        size,
        filter,
        sort,
        unpaged,
      },
    })
    .then(({ data: { id } }) => ({ reportId: id }))
    .catch(errorHandler);

/**
 * Generate reports with all mobile/desktop verification requests
 */
const requestVerificationRequests = axios => type => ({
  page,
  size,
  filter = "",
  sort = "",
  from,
  to,
  unpaged = true,
}) =>
  axios
    .get(`/tps/api/report/verification-requests/${type}`, {
      params: {
        page,
        size,
        filter,
        sort: SortAdapter(sort),
        from,
        to,
        unpaged,
      },
    })
    .then(({ data: { id } }) => ({ reportId: id }))
    .catch(errorHandler);

// ReportResource.getAllMobileVerificationRequests
const requestVerificationRequestsMobile = axios =>
  requestVerificationRequests(axios)("mobile");
// ReportResource.getAllDesktopVerificationRequests
const requestVerificationRequestsDesktop = axios =>
  requestVerificationRequests(axios)("desktop");
// ReportResource.getAllWebVerificationRequests
const requestVerificationRequestsWeb = axios =>
  requestVerificationRequests(axios)("web");

/**
 * Generate reports with all mobile/desktop iroha accounts
 */
const requestAllUser = axios => type => ({
  page,
  size,
  filter = "",
  sort = "",
  kyc = "ALL",
  from,
  to,
  unpaged = true,
}) =>
  axios
    .get(`/tps/api/report/fst-iroha-accounts/${type}`, {
      params: {
        page,
        size,
        filter,
        sort: SortAdapter(sort),
        kyc,
        from,
        to,
        unpaged,
      },
    })
    .then(({ data: { id } }) => ({ reportId: id }))
    .catch(errorHandler);

// ReportResource.getAllMobileIrohaAccounts
const requestAllMobileUser = axios => requestAllUser(axios)("mobile");
// ReportResource.getAllDesktopIrohaAccounts
const requestAllDesktopUser = axios => requestAllUser(axios)("desktop");
// ReportResource.getAllWebIrohaAccounts
const requestAllWebUser = axios => requestAllUser(axios)("web");

/**
 * Generate reports with all mobile/desktop kyc limits
 */
const requestLimitsList = axios => type => ({
  page,
  size,
  filter = "",
  sort = "",
  from,
  to,
  unpaged = true,
}) =>
  axios
    .get(`/tps/api/report/kyc-limits/${type}`, {
      params: {
        page,
        size,
        filter,
        sort,
        from,
        to,
        unpaged,
      },
    })
    .then(({ data: { id } }) => ({ reportId: id }))
    .catch(errorHandler);

// ReportResource.getAllMobileKycLimits
const requestLimitsListMobile = axios => requestLimitsList(axios)("mobile");
// ReportResource.getAllDesktopKycLimits
const requestLimitsListDesktop = axios => requestLimitsList(axios)("desktop");
// ReportResource.getAllWebKycLimits
const requestLimitsListWeb = axios => requestLimitsList(axios)("web");

//
/**
 * Check status of report generation
 */
const getStatus = axios => reportId =>
  axios
    .get(`/report-service/api/report/status/${reportId}`)
    .then(({ data }) => data)
    .catch(errorHandler);

const getExportStatus = axios => reportId =>
  axios
    .get(`/report-service/api/v2/report/statuses`)
    .then(({ data }) => data)
    .catch(errorHandler);

const getPeriodOptions = axios => reportId =>
  axios
    .get(`/report-service/api/v2/export/account_statement/period/options`)
    .then(({ data }) => data)
    .catch(errorHandler);

// ReportResource.getCsvReport
/**
 * Get report
 */
const getReport = axios => reportId =>
  axios
    .get(`/report-service/api/report/csv/${reportId}`, {
      headers: {
        Accept: "application/octet-stream",
      },
    })
    .then(({ data }) => data)
    .catch(errorHandler);

/**
 * Wait until a request is resolved or rejected
 */
const waitUntilProcessed = axios => reportId => {
  const isReportReady = status => status === "CREATED";
  const isBeingProcessed = status => {
    return ["ACCEPTED", "PENDING", "IN_PROGRESS"].includes(status);
  };
  const isRejected = status => {
    return ["REJECTED", "ERROR", "REPORT_ID_NOT_FOUND_IN_CACHE"].includes(
      status,
    );
  };

  return retry(
    async () => {
      let status;

      try {
        ({ status } = await getStatus(axios)(reportId));
      } catch (err) {
        throw new retry.AbortError(err);
      }

      if (isBeingProcessed(status)) {
        // will emit retry until retry attempts are not over
        // then promise will be rejected with the error below
        throw new Error(
          "The report has been processing too long. Try again later.",
        );
      }

      if (isRejected(status)) {
        throw new retry.AbortError(
          `Request has failed in processing: ${status}`,
        );
      }

      if (isReportReady(status)) {
        return;
      }

      throw new retry.AbortError(
        `Received an unknown report status: ${status}`,
      );
    },
    {
      // the maximum waiting time is about 6 minutes ( minTimeout * Math.pow(2, retries) seconds )
      retries: 7,
      minTimeout: 3 * 1000,
    },
  );
};

export default {
  requestCancelExportedFile: requestCancelExportedFile(axiosInstance),
  requestDownloadExportedFile: requestDownloadExportedFile(axiosInstance),
  requestExports: requestExports(axiosInstance),
  getExportStatus: getExportStatus(axiosInstance),
  requestAccountStatement: requestAccountStatement(axiosInstance),
  requestIncomingTransactions: requestIncomingTransactions(axiosInstance),
  requestOutgoingTransactions: requestOutgoingTransactions(axiosInstance),
  requestAccountTransactions: requestAccountTransactions(axiosInstance),
  requestListUserUnderDomain: requestListUserUnderDomain(axiosInstance),
  requestHistory: requestHistory(axiosInstance),
  requestVerificationRequestsMobile: requestVerificationRequestsMobile(
    axiosInstance,
  ),
  requestVerificationRequestsDesktop: requestVerificationRequestsDesktop(
    axiosInstance,
  ),
  requestVerificationRequestsWeb: requestVerificationRequestsWeb(axiosInstance),
  requestAllMobileUser: requestAllMobileUser(axiosInstance),
  requestAllDesktopUser: requestAllDesktopUser(axiosInstance),
  requestAllWebUser: requestAllWebUser(axiosInstance),
  requestLimitsListMobile: requestLimitsListMobile(axiosInstance),
  requestLimitsListDesktop: requestLimitsListDesktop(axiosInstance),
  requestLimitsListWeb: requestLimitsListWeb(axiosInstance),

  getStatus: getStatus(axiosInstance),
  getReport: getReport(axiosInstance),
  getPeriodOptions: getPeriodOptions(axiosInstance),
  waitUntilProcessed: waitUntilProcessed(axiosInstance),
};
